/**
 * 串题组 - API 封装
 *
 * 提供串题组管理相关的 API 接口
 *
 * @author AI Assistant
 * @created 2025-12-07
 */

import httpRequest from '@/utils/api'
import type {
  TopicGroup,
  TopicGroupMember,
  TopicGroupAnswer,
  TopicGroupMemberNote,
  TopicGroupListParams,
  TopicGroupPageResponse,
  CreateTopicGroupRequest,
  UpdateTopicGroupRequest,
  AddMemberRequest,
  AddMembersBatchRequest,
  CreateAnswerRequest,
  UpdateAnswerRequest,
  UpsertNoteRequest,
  TopicUsage,
} from '@/types/topic/topicGroup'

enum API {
  // 串题组 CRUD
  createGroup = '/admin/topic-groups',
  getGroups = '/admin/topic-groups',
  getGroup = '/admin/topic-groups/:groupId',
  updateGroup = '/admin/topic-groups/:groupId',
  deleteGroup = '/admin/topic-groups/:groupId',
  // 组成员管理
  addMember = '/admin/topic-groups/:groupId/members',
  addMembersBatch = '/admin/topic-groups/:groupId/members/batch',
  removeMember = '/admin/topic-groups/:groupId/members/:memberId',
  // 串题答案管理
  createAnswer = '/admin/topic-groups/:groupId/answer',
  updateAnswer = '/admin/topic-groups/:groupId/answer',
  deleteAnswer = '/admin/topic-groups/:groupId/answer',
  // 话题微调管理
  createNote = '/admin/topic-groups/members/:memberId/note',
  updateNote = '/admin/topic-groups/members/:memberId/note',
  deleteNote = '/admin/topic-groups/members/:memberId/note',
  // 话题使用状态
  getTopicsUsage = '/admin/topics/usage',
}

// ==================== 串题组 CRUD ====================

/**
 * 创建串题组
 *
 * @param data 创建请求数据
 * @returns 串题组详情
 */
export const createTopicGroupAPI = (data: CreateTopicGroupRequest) => {
  return httpRequest.post<TopicGroup>({
    url: API.createGroup,
    data,
  })
}

/**
 * 获取串题组列表（分页）
 *
 * @param params 查询参数
 * @returns 分页列表响应
 */
export const getTopicGroupsAPI = (params?: TopicGroupListParams) => {
  const queryParams: Record<string, any> = {
    page: params?.page || 1,
    limit: params?.limit || 20,
  }

  if (params?.categoryId) {
    queryParams.category_id = params.categoryId
  }

  return httpRequest.get<TopicGroupPageResponse>({
    url: API.getGroups,
    params: queryParams,
  })
}

/**
 * 获取串题组详情
 *
 * @param groupId 串题组 ID
 * @returns 串题组详情
 */
export const getTopicGroupAPI = (groupId: number) => {
  return httpRequest.get<TopicGroup>({
    url: API.getGroup.replace(':groupId', String(groupId)),
  })
}

/**
 * 更新串题组
 *
 * @param groupId 串题组 ID
 * @param data 更新请求数据
 * @returns 串题组详情
 */
export const updateTopicGroupAPI = (groupId: number, data: UpdateTopicGroupRequest) => {
  return httpRequest.put<TopicGroup>({
    url: API.updateGroup.replace(':groupId', String(groupId)),
    data,
  })
}

/**
 * 删除串题组
 *
 * @param groupId 串题组 ID
 * @returns 删除结果
 */
export const deleteTopicGroupAPI = (groupId: number) => {
  return httpRequest.delete<null>({
    url: API.deleteGroup.replace(':groupId', String(groupId)),
  })
}

// ==================== 组成员管理 ====================

/**
 * 添加组成员
 *
 * @param groupId 串题组 ID
 * @param data 添加成员请求
 * @returns 成员详情
 */
export const addMemberAPI = (groupId: number, data: AddMemberRequest) => {
  return httpRequest.post<TopicGroupMember>({
    url: API.addMember.replace(':groupId', String(groupId)),
    data,
  })
}

/**
 * 批量添加组成员
 *
 * @param groupId 串题组 ID
 * @param data 批量添加请求
 * @returns 成员列表
 */
export const addMembersBatchAPI = (groupId: number, data: AddMembersBatchRequest) => {
  return httpRequest.post<TopicGroupMember[]>({
    url: API.addMembersBatch.replace(':groupId', String(groupId)),
    data,
  })
}

/**
 * 移除组成员
 *
 * @param groupId 串题组 ID
 * @param memberId 成员 ID
 * @returns 删除结果
 */
export const removeMemberAPI = (groupId: number, memberId: number) => {
  return httpRequest.delete<null>({
    url: API.removeMember
      .replace(':groupId', String(groupId))
      .replace(':memberId', String(memberId)),
  })
}

// ==================== 串题答案管理 ====================

/**
 * 创建串题答案
 *
 * @param groupId 串题组 ID
 * @param data 创建答案请求
 * @returns 答案详情
 */
export const createAnswerAPI = (groupId: number, data: CreateAnswerRequest) => {
  return httpRequest.post<TopicGroupAnswer>({
    url: API.createAnswer.replace(':groupId', String(groupId)),
    data,
  })
}

/**
 * 更新串题答案
 *
 * @param groupId 串题组 ID
 * @param data 更新答案请求
 * @returns 答案详情
 */
export const updateAnswerAPI = (groupId: number, data: UpdateAnswerRequest) => {
  return httpRequest.put<TopicGroupAnswer>({
    url: API.updateAnswer.replace(':groupId', String(groupId)),
    data,
  })
}

/**
 * 删除串题答案
 *
 * @param groupId 串题组 ID
 * @returns 删除结果
 */
export const deleteAnswerAPI = (groupId: number) => {
  return httpRequest.delete<null>({
    url: API.deleteAnswer.replace(':groupId', String(groupId)),
  })
}

// ==================== 话题微调管理 ====================

/**
 * 创建话题微调
 *
 * @param memberId 成员 ID
 * @param data 创建微调请求
 * @returns 微调详情
 */
export const createNoteAPI = (memberId: number, data: UpsertNoteRequest) => {
  return httpRequest.post<TopicGroupMemberNote>({
    url: API.createNote.replace(':memberId', String(memberId)),
    data,
  })
}

/**
 * 更新话题微调
 *
 * @param memberId 成员 ID
 * @param data 更新微调请求
 * @returns 微调详情
 */
export const updateNoteAPI = (memberId: number, data: UpsertNoteRequest) => {
  return httpRequest.put<TopicGroupMemberNote>({
    url: API.updateNote.replace(':memberId', String(memberId)),
    data,
  })
}

/**
 * 删除话题微调
 *
 * @param memberId 成员 ID
 * @returns 删除结果
 */
export const deleteNoteAPI = (memberId: number) => {
  return httpRequest.delete<null>({
    url: API.deleteNote.replace(':memberId', String(memberId)),
  })
}

// ==================== 话题使用状态 ====================

/**
 * 获取话题使用状态
 *
 * 用于在添加话题到串题组时，展示每个话题被哪些串题组使用
 *
 * @param categoryId 分类 ID（可选，用于筛选）
 * @returns 话题使用状态列表
 */
export const getTopicsUsageAPI = (categoryId?: number) => {
  const params: Record<string, any> = {}
  if (categoryId !== undefined) {
    params.category_id = categoryId
  }

  return httpRequest.get<TopicUsage[]>({
    url: API.getTopicsUsage,
    params,
  })
}
